import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Typography, Box, Divider, CircularProgress,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";

const TYPES = [
  { value: "Voice", label: "Voice", field: "balance", rateField: "voice_rate" },
  { value: "Whatsapp", label: "WhatsApp", field: "whatsapp_balance", rateField: "whatsapp_rate" },
  { value: "Text", label: "Text SMS", field: "text_balance", rateField: "text_rate" },
];

const ConvertCredits = ({ open, onClose, onSuccess }) => {
  const [type, setType] = useState("Voice");
  const [rupeeAmount, setRupeeAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);

  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (open) {
      fetchProfile();
      setType("Voice");
      setRupeeAmount("");
    }
  }, [open]);

  const fetchProfile = () => {
    axios
      .get(`${BASE_URL}auth/view-profile`, {
        headers: { "x-access-token": token, version: "1.0.0", "Content-Type": "Application/json" },
      })
      .then((res) => {
        if (res.data.success) setProfile(res.data.data);
      })
      .catch(() => {});
  };

  const selected = TYPES.find((t) => t.value === type);
  const rate = profile ? parseFloat(profile[selected.rateField]) || 0 : 0;
  const currentCredits = profile ? parseInt(profile[selected.field]) || 0 : 0;
  const rupeeBalance = profile ? parseFloat(profile.rupee_balance) || 0 : 0;
  const amount = parseFloat(rupeeAmount) || 0;
  const creditsPreview = rate > 0 && amount > 0 ? Math.floor(amount / rate) : 0;

  const handleSubmit = async () => {
    if (!rupeeAmount || amount <= 0) {
      toast.error("Enter a valid ₹ amount");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${BASE_URL}admin/convertBalanceToCredits`,
        { type, rupee_amount: amount },
        { headers: { "x-access-token": token, version: "1.0.0", "Content-Type": "Application/json" } }
      );
      if (res.data.success) {
        toast.success(res.data.message);
        onSuccess && onSuccess();
        onClose();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Conversion failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Convert ₹ Balance to Credits</DialogTitle>
      <DialogContent>
        {/* Current balances strip */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          {TYPES.map((t) => (
            <Box
              key={t.value}
              sx={{
                flex: 1, minWidth: 120, p: 1.5, borderRadius: 1,
                border: "1px solid #e0e0e0", textAlign: "center",
                bgcolor: type === t.value ? "#f0f4ff" : "#fafafa",
              }}
            >
              <Typography variant="caption" color="text.secondary">{t.label} Credits</Typography>
              <Typography variant="h6" fontWeight={600}>
                {profile ? parseInt(profile[t.field]) || 0 : "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Rate: {profile && parseFloat(profile[t.rateField]) > 0
                  ? `₹${parseFloat(profile[t.rateField])}/credit`
                  : "not set"}
              </Typography>
            </Box>
          ))}
          <Box sx={{ flex: 1, minWidth: 120, p: 1.5, borderRadius: 1, border: "1px solid #e0e0e0", textAlign: "center", bgcolor: "#fffbf0" }}>
            <Typography variant="caption" color="text.secondary">₹ Balance</Typography>
            <Typography variant="h6" fontWeight={600} color="warning.main">
              ₹{rupeeBalance.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <TextField
          select fullWidth label="Convert to" value={type}
          onChange={(e) => { setType(e.target.value); setRupeeAmount(""); }}
          sx={{ mb: 2 }}
        >
          {TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth label="₹ Amount to convert" type="number"
          value={rupeeAmount}
          onChange={(e) => setRupeeAmount(e.target.value)}
          inputProps={{ min: 1 }}
          sx={{ mb: 2 }}
          helperText={rate === 0 ? "No rate configured for this type. Contact admin." : `Rate: ₹${rate} per credit`}
          error={rate === 0}
        />

        {creditsPreview > 0 && (
          <Box sx={{ p: 2, bgcolor: "#e8f5e9", borderRadius: 1, border: "1px solid #c8e6c9" }}>
            <Typography fontWeight={600} color="success.dark">
              You will receive {creditsPreview} {selected.label} credits
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ₹{amount.toFixed(2)} ÷ ₹{rate}/credit = {creditsPreview} credits
            </Typography>
            <Typography variant="body2" color="text.secondary">
              New balance after: ₹{Math.max(0, rupeeBalance - amount).toFixed(2)} | {currentCredits + creditsPreview} {selected.label} credits
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained" onClick={handleSubmit}
          disabled={loading || rate === 0 || creditsPreview < 1}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Convert
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConvertCredits;
